import type { PhasePowerSourceConfig, ProjectIntent, ProjectPhase } from '../../types/siteRag';
import type { SiteEntity } from '../../types/site';
import type { GenerationImpactScenarioInput, GenerationImpactScenarioSnapshot } from '../../types/generationImpact';
import type { GridLedStandbyPlanningInput } from '../../types/gridLedStandbyPlanning';
import { generationProjectInputChangesSchema, generationProjectInputBindingSchema, type GenerationProjectInputBinding, type GenerationProjectInputEditScope } from '../../types/generationProjectInputSync';
import { effectivePhaseCapacity, reconcilePowerRequirement, reconcilePowerRequirementWithPhases, withPhaseCapacityMW, withPhaseInServiceDate, withPowerRequirementMW } from '../projectIntent';
import { hashGenerationImpactValue, generationImpactScenarioIntegrityReason } from './integrity';
import { decodeFirestoreNestedArrays } from './firestoreNestedArrayCodec';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';

/** Preserve the one declared PUE field's legacy value without reviving an
 * explicitly cleared input or deriving a design/default PUE. */
export function declaredSharedProjectPue(intent: ProjectIntent | undefined | null): number | null {
  if (isIntentFieldExplicitlyUnknown(intent, 'pueValue')) return null;
  const value = intent?.pueValue ?? intent?.pueTarget;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A phase's power configuration as the shared-input hash sees it. The
 * regulatory-only `exportMode` has no Generation Impact effect, so it is left
 * out; a configuration without it hashes byte-identically to before. */
function hashedPowerSourceConfig(config: PhasePowerSourceConfig | undefined): Omit<PhasePowerSourceConfig, 'exportMode'> | null {
  if (config == null) return null;
  const { exportMode: _regulatoryOnly, ...rest } = config;
  return rest;
}

/** Workflow identity only: neither source provenance nor a physics hash. */
export function generationProjectInputHash(intent: ProjectIntent | undefined | null): string {
  return hashGenerationImpactValue({
    loadMW: intent?.powerRequirementMW ?? null, loadBucket: intent?.powerRequirement ?? 'undecided',
    loadBasis: intent?.powerBasis ?? null, loadBasisExplicitlyUnknown: isIntentFieldExplicitlyUnknown(intent, 'powerBasis'), pue: declaredSharedProjectPue(intent),
    workload: intent?.workloadProfile ?? null, cooling: intent?.coolingStrategy ?? 'undecided',
    heatRejection: intent?.bopHeatRejection ?? 'unknown',
    phases: [...(intent?.phases ?? [])].sort((a, b) => a.sequence - b.sequence).map(phase => ({
      id: phase.id, sequence: phase.sequence, loadMW: phase.capacityMW,
      loadBucket: phase.capacityBucket ?? null, operationTargetDate: phase.targetInServiceDate,
      powerSource: phase.powerSource, config: hashedPowerSourceConfig(phase.powerSourceConfig),
    })),
  });
}

/** Exactly the main active workflow may declare shared project inputs. */
export function assertGenerationProjectInputEditScope(site: Pick<SiteEntity, 'generationAssessmentSelection' | 'projectIntent'>,
  editing: GenerationProjectInputEditScope): void {
  const active = site.generationAssessmentSelection;
  const legacyId = site.projectIntent?.promotedGenerationScenarioId;
  if (!active && !legacyId) {
    if (editing !== null) throw new Error('Inactive saved scenarios cannot update shared project inputs.');
    return;
  }
  if (!editing) throw new Error('Open the active assessment before updating shared project inputs.');
  const expected = active ?? { kind: 'generation-impact-v1', assessmentId: legacyId,
    inputHash: site.projectIntent?.promotedGenerationInputHash, resultHash: site.projectIntent?.promotedGenerationResultHash };
  if (editing.kind !== expected.kind || editing.assessmentId !== expected.assessmentId
    || editing.inputHash !== expected.inputHash || editing.resultHash !== expected.resultHash) {
    throw new Error('Only the exact active assessment may update shared project inputs.');
  }
}

function phaseForEdit(intent: ProjectIntent, id: string): ProjectPhase {
  const phase = intent.phases?.find(candidate => candidate.id === id);
  if (!phase) throw new Error('The edited project phase no longer exists.');
  return phase;
}

/** Patch only an actual field edit against the current complete Intent. This
 * does not fill gaps, adopt recommendations or restore reset/absent fields. */
export function applyGenerationProjectInputChanges(previous: ProjectIntent | undefined,
  candidate: unknown): ProjectIntent {
  const changes = generationProjectInputChangesSchema.parse(candidate);
  let next: ProjectIntent = structuredClone(previous ?? { powerRequirement: 'undecided', coolingStrategy: 'undecided', nuclearInterest: 'undecided', timeline: 'undecided', priority: 'undecided' });
  const sources = { ...next.fieldSource };
  const mark = (field: string, value: unknown) => { sources[field] = value == null || value === 'unknown' || value === 'undecided' ? 'user-unknown' : 'user'; };
  const phaseChanged = changes.phaseId != null && (changes.loadMW !== undefined || changes.operationTargetDate !== undefined || changes.gridConnection !== undefined || changes.powerComponents !== undefined);
  if (phaseChanged) {
    let phase = phaseForEdit(next, changes.phaseId!);
    if (changes.loadMW !== undefined) {
      phase = withPhaseCapacityMW(phase, changes.loadMW);
      mark(`phases.${phase.id}.capacityMW`, changes.loadMW);
    }
    if (changes.operationTargetDate !== undefined) {
      phase = changes.operationTargetDate == null ? { ...phase, targetInServiceDate: null, codTimeline: 'undecided' }
        : withPhaseInServiceDate(phase, changes.operationTargetDate);
      mark(`phases.${phase.id}.targetInServiceDate`, changes.operationTargetDate);
    }
    if (changes.gridConnection !== undefined || changes.powerComponents !== undefined) {
      const config: PhasePowerSourceConfig = phase.powerSourceConfig ?? { components: [], gridConnection: 'undecided' };
      const components = changes.powerComponents ?? config.components;
      phase = { ...phase,
        ...(changes.powerComponents !== undefined ? { powerSource: components.length === 0 ? 'undecided' : components.length > 1 ? 'hybrid' : components[0] === 'grid' ? 'grid' : components[0] === 'smr' ? 'smr' : components[0] === 'large-nuclear' ? 'large-nuclear' : components[0] === 'solar-bess' ? 'solar-bess' : components[0] === 'wind-bess' ? 'wind-bess' : 'undecided' } : {}),
        powerSourceConfig: { ...config, components: [...components],
          ...(changes.gridConnection !== undefined ? { gridConnection: changes.gridConnection } : {}),
          // A selected technology is a choice, not an ISO rating or inferred
          // AND mix. Preserve explicit prior parameters only for retained keys.
          ...(changes.powerComponents !== undefined ? { componentParams: Object.fromEntries(Object.entries(config.componentParams ?? {}).filter(([key]) => components.includes(key as typeof components[number]))) } : {}),
        } };
      mark(`phases.${phase.id}.powerSourceConfig`, components.length ? 'choice' : null);
    }
    next.phases = next.phases!.map(current => current.id === phase.id ? phase : current);
    const ultimate = [...next.phases].sort((a, b) => b.sequence - a.sequence)[0];
    if (changes.loadMW !== undefined && ultimate?.id === phase.id) {
      if (changes.loadMW == null) { delete next.powerRequirementMW; next.powerRequirement = 'undecided'; }
      else next = withPowerRequirementMW(next, changes.loadMW);
      delete next.maxPowerStudy; delete next.powerSizingMode;
      mark('powerRequirementMW', changes.loadMW);
    }
  } else if (changes.loadMW !== undefined) {
    if (next.phases?.length) throw new Error('Select an exact phase before changing a phased load.');
    if (changes.loadMW == null) { delete next.powerRequirementMW; next.powerRequirement = 'undecided'; }
    else next = withPowerRequirementMW(next, changes.loadMW);
    mark('powerRequirementMW', changes.loadMW);
    delete next.maxPowerStudy; delete next.powerSizingMode;
  }
  const enteredBasis = changes.loadBasis === undefined ? previous?.powerBasis : changes.loadBasis;
  if (changes.loadMW != null && enteredBasis !== 'facility' && enteredBasis !== 'it-load') {
    throw new Error('Declare the entered load basis explicitly.');
  }
  if (changes.loadBasis !== undefined) {
    if (changes.loadBasis == null) delete next.powerBasis; else next.powerBasis = changes.loadBasis;
    mark('powerBasis', changes.loadBasis);
  }
  if (changes.pue !== undefined) {
    if (changes.pue == null) delete next.pueValue; else next.pueValue = changes.pue;
    delete next.pueTarget;
    mark('pueValue', changes.pue);
  }
  if (changes.workloadProfile !== undefined) {
    if (changes.workloadProfile == null) delete next.workloadProfile; else next.workloadProfile = changes.workloadProfile;
    mark('workloadProfile', changes.workloadProfile);
  }
  if (changes.coolingStrategy !== undefined) { next.coolingStrategy = changes.coolingStrategy; mark('coolingStrategy', changes.coolingStrategy); }
  if (changes.heatRejection !== undefined) { next.bopHeatRejection = changes.heatRejection; mark('bopHeatRejection', changes.heatRejection); }
  next = reconcilePowerRequirement(reconcilePowerRequirementWithPhases(next).intent);
  next.fieldSource = sources;
  // Semantic no-op excludes new provenance/timestamps. Repeated blur/hydration
  // of an unchanged value must not invalidate reports or trigger a sync loop.
  if (generationProjectInputHash(next) === generationProjectInputHash(previous)) return previous ?? next;
  return next;
}

function declaredFacilityMW(intent: ProjectIntent | undefined, phaseId?: string): number | null {
  // Historical declared demand with no basis retains its documented facility
  // interpretation. A newly explicit Unknown is not that legacy convention.
  // An IT basis remains IT; this bridge never supplies a default PUE conversion.
  if (!intent || isIntentFieldExplicitlyUnknown(intent, 'powerBasis')
    || (intent.powerBasis != null && intent.powerBasis !== 'facility')) return null;
  const phase = phaseId ? intent.phases?.find(value => value.id === phaseId) : undefined;
  const value = phase ? effectivePhaseCapacity(phase).isExact ? effectivePhaseCapacity(phase).mw : null : intent.powerRequirementMW;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

/** Intent changes create an UNSAVED draft view, never overwrite the stored
 * scenario/result or rebuild equipment from an ambiguous technology choice. */
export function overlayIntentOnGenerationDraft(input: GenerationImpactScenarioInput, intent: ProjectIntent | undefined): GenerationImpactScenarioInput {
  const load = declaredFacilityMW(intent, input.targetPhaseId);
  const phase = input.targetPhaseId ? intent?.phases?.find(value => value.id === input.targetPhaseId) : undefined;
  const grid = phase?.powerSourceConfig?.gridConnection;
  if ((load == null || input.facilityLoadMW === load) && (grid == null || input.gridConnection === grid)) return input;
  return { ...input, ...(load != null ? { facilityLoadMW: load } : {}), ...(grid != null ? { gridConnection: grid } : {}) };
}

export function overlayIntentOnPlanningDraft(input: GridLedStandbyPlanningInput, intent: ProjectIntent | undefined): GridLedStandbyPlanningInput {
  const phases = input.phases.map(phase => {
    const current = intent?.phases?.find(candidate => candidate.id === phase.phaseId);
    return { ...phase, facilityMW: declaredFacilityMW(intent, phase.phaseId), operationTargetDate: current?.targetInServiceDate ?? null };
  });
  const pue = declaredSharedProjectPue(intent);
  if (hashGenerationImpactValue({ phases, pue }) === hashGenerationImpactValue({ phases: input.phases, pue: input.annual.pue })) return input;
  const next = { ...input, phases, annual: { ...input.annual, pue } };
  // The old reviewed numeric/source pair must not certify a newly edited value.
  const changed = new Set<string>();
  phases.forEach((phase, index) => {
    if (phase.facilityMW !== input.phases[index]?.facilityMW) changed.add(`phases.${index}.facilityMW`);
    if (phase.operationTargetDate !== input.phases[index]?.operationTargetDate) changed.add(`phases.${index}.operationTargetDate`);
  });
  if (pue !== input.annual.pue) changed.add('annual.pue');
  return { ...next, sources: input.sources.filter(source => !changed.has(source.field)) };
}

/** Optional workflow binding is outside the original input/result physics
 * hashes. Legacy snapshots did not record this binding, so their original
 * applicability gates remain authoritative until a newly bound revision is
 * selected. Absence cannot establish when a historical Intent changed. */
export function generationScenarioProjectInputStalenessReason(intent: ProjectIntent | undefined | null,
  snapshot: GenerationImpactScenarioSnapshot | null | undefined, binding?: GenerationProjectInputBinding): string | null {
  if (!snapshot) return null;
  if (binding != null) {
    const parsed = generationProjectInputBindingSchema.safeParse(binding);
    if (!parsed.success) return 'Shared project input binding is invalid; refresh and select a new assessment revision.';
    if (parsed.data.assessmentId === snapshot.id && parsed.data.inputHash === snapshot.inputHash && parsed.data.resultHash === snapshot.resultHash) {
      return parsed.data.sharedInputHash === generationProjectInputHash(intent) ? null
        : 'Project inputs changed; recalculate and select a new assessment revision.';
    }
  }
  if (snapshot.sharedProjectInputHash != null) return snapshot.sharedProjectInputHash === generationProjectInputHash(intent)
    ? null : 'Project inputs changed; recalculate and select a new assessment revision.';
  return null;
}

/** Called inside the actual Intent-write transaction, before applying its
 * shared changes. Withhold when the exact stored active result cannot prove
 * its identity. Repeated edits retain the first basis, never rebase stale data. */
export function activeProjectInputBindingBeforeEdit(site: Pick<SiteEntity, 'projectIntent' | 'generationAssessmentSelection'
  | 'promotedGenerationScenarioSnapshot' | 'generationProjectInputBinding'>): GenerationProjectInputBinding | undefined {
  if (site.generationAssessmentSelection?.kind === 'grid-led-standby-planning') return undefined;
  const snapshot = site.promotedGenerationScenarioSnapshot ? decodeFirestoreNestedArrays(site.promotedGenerationScenarioSnapshot) : undefined;
  const intent = site.projectIntent;
  if (!snapshot || snapshot.id !== intent?.promotedGenerationScenarioId
    || snapshot.inputHash !== intent.promotedGenerationInputHash || snapshot.resultHash !== intent.promotedGenerationResultHash
    || generationImpactScenarioIntegrityReason(snapshot)) return undefined;
  const pointer = site.generationAssessmentSelection;
  if (pointer && (pointer.assessmentId !== snapshot.id || pointer.inputHash !== snapshot.inputHash || pointer.resultHash !== snapshot.resultHash
    || pointer.selectedAt !== snapshot.promotedAt)) return undefined;
  const prior = generationProjectInputBindingSchema.safeParse(site.generationProjectInputBinding);
  if (site.generationProjectInputBinding != null && !prior.success) return undefined;
  if (prior.success && prior.data.assessmentId === snapshot.id && prior.data.inputHash === snapshot.inputHash && prior.data.resultHash === snapshot.resultHash) return prior.data;
  const sourceHash = snapshot.sharedProjectInputHash ?? generationProjectInputHash(intent);
  if (!/^[a-f\d]{64}$/.test(sourceHash)) return undefined;
  return { schemaVersion: 1, kind: 'generation-impact-v1', assessmentId: snapshot.id,
    inputHash: snapshot.inputHash, resultHash: snapshot.resultHash, sharedInputHash: sourceHash };
}
